<template>
  <div class="pa-2">
    <v-card class="mx-auto" outlined>
      <v-list-item three-line>
        <v-list-item-content>
          <div class="overline mb-4">
            {{ algoParams.timestamp | moment("from") }}
          </div>
          <v-list-item-title class="headline mb-1"
            >Review Algorithm Parameters
            <v-chip class="ma-2">
              Carbohydrate counting: <b>{{ isAM ? "ON" : "OFF" }}</b>
            </v-chip>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-container class="text-center body-1">
        <v-row no-gutters class="font-weight-bold">
          <v-col cols="2" class="py-2">
            Params
          </v-col>
          <v-col cols="2" class="py-2">
            Basal Dose (U)
          </v-col>
          <v-col cols="2" class="py-2"> Breakfast ({{ doseUnit }}) </v-col>
          <v-col cols="2" class="py-2"> Lunch ({{ doseUnit }}) </v-col>
          <v-col cols="2" class="py-2"> Dinner ({{ doseUnit }}) </v-col>
          <v-col cols="2" class="py-2"> Bedtime ({{ doseUnit }}) </v-col>
        </v-row>

        <v-row no-gutters>
          <v-col cols="2" class="py-2">
            Current
          </v-col>
          <v-col cols="2" class="py-2">
            {{ params.basaDose }}
          </v-col>
          <v-col cols="2" class="py-2">
            {{ params[dosePrefix].breakfast }}
          </v-col>
          <v-col cols="2" class="py-2">
            {{ params[dosePrefix].lunch }}
          </v-col>
          <v-col cols="2" class="py-2">
            {{ params[dosePrefix].supper }}
          </v-col>
          <v-col cols="2" class="py-2">
            {{ params[dosePrefix].bedtime }}
          </v-col>
        </v-row>

        <v-row no-gutters>
          <v-col cols="2" class="py-2">
            Recommended
          </v-col>
          <v-col cols="2" class="py-2">
            <v-chip class="px-4" :class="getChipClass('basaDose')">
              {{ algoParams[`basalDose`] }}
            </v-chip>
          </v-col>
          <v-col cols="2" class="py-2">
            <v-chip
              class="px-4"
              :class="getChipClass('breakfast', 'Breakfast')"
            >
              {{ algoParams[`${algoPrefix}Breakfast`] }}
            </v-chip>
          </v-col>
          <v-col cols="2" class="py-2">
            <v-chip class="px-4" :class="getChipClass('lunch', 'Lunch')">
              {{ algoParams[`${algoPrefix}Lunch`] }}
            </v-chip>
          </v-col>
          <v-col cols="2" class="py-2">
            <v-chip class="px-4" :class="getChipClass('supper', 'Dinner')">
              {{ algoParams[`${algoPrefix}Dinner`] }}
            </v-chip>
          </v-col>
          <v-col cols="2" class="py-2">
            <v-chip class="px-4" :class="getChipClass('bedtime', 'Bedtime')">
              {{ algoParams[`${algoPrefix}Bedtime`] }}
            </v-chip>
          </v-col>
        </v-row>

        <v-row no-gutters v-if="paramsToBeSent">
          <v-col cols="2" class="py-2 font-weight-bold">
            To Review
          </v-col>
          <v-col cols="2" class="pa-2">
            <v-text-field
              outlined
              dense
              hide-details="auto"
              class="input-text-center"
              v-model="paramsToBeSent.basaDose"
              :disabled="
                !(
                  userRole === 'admin' ||
                  userRole === 'rn' ||
                  userRole === 'md'
                )
              "
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="pa-2">
            <v-text-field
              outlined
              dense
              hide-details="auto"
              class="input-text-center"
              v-model="paramsToBeSent.breakfast"
              :disabled="
                !(
                  userRole === 'admin' ||
                  userRole === 'rn' ||
                  userRole === 'md'
                )
              "
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="pa-2">
            <v-text-field
              outlined
              dense
              hide-details="auto"
              class="input-text-center"
              v-model="paramsToBeSent.lunch"
              :disabled="
                !(
                  userRole === 'admin' ||
                  userRole === 'rn' ||
                  userRole === 'md'
                )
              "
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="pa-2">
            <v-text-field
              outlined
              dense
              hide-details="auto"
              class="input-text-center"
              v-model="paramsToBeSent.supper"
              :disabled="
                !(
                  userRole === 'admin' ||
                  userRole === 'rn' ||
                  userRole === 'md'
                )
              "
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="pa-2">
            <v-text-field
              outlined
              dense
              hide-details="auto"
              class="input-text-center"
              v-model="paramsToBeSent.bedtime"
              :disabled="
                !(
                  userRole === 'admin' ||
                  userRole === 'rn' ||
                  userRole === 'md'
                )
              "
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row no-gutters v-if="paramsToBeSent">
          <v-col cols="2"> </v-col>
          <v-col cols="10" class="px-2">
            <v-textarea
              v-model="notes"
              prepend-inner-icon="comment"
              label="Notes"
              rows="1"
              outlined
              auto-grow
              persistent-hint
              hint="You can write a note to the patient's profile when flagging to endo"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-container>

      <v-divider></v-divider>

      <v-card-actions
        v-if="userRole === 'admin' || userRole === 'rn' || userRole === 'md'"
      >
        <v-spacer></v-spacer>
        <v-btn
          text
          color="warning"
          :disabled="emailAlreadySent"
          @click="flagReviewParams"
        >
          <v-icon left>mdi-flag</v-icon>
          {{
            emailAlreadySent
              ? "Flagged & Email sent"
              : "Flag & Email " +
                (groupDetails.name ? groupDetails.name : "[unassigned]")
          }}
        </v-btn>
        <v-btn color="primary" @click="approveReviewParams">
          <v-icon left>mdi-check</v-icon>
          Approve & Send to Patient
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped>
.row-has-changed {
  color: #ff5252;
  font-weight: bold;
}
</style>

<script>
import firebase from "firebase/app";
import moment from "moment";
import { EventBus } from "../main";

import { mapState } from "vuex";

export default {
  name: "review-card",
  props: ["algoParams", "groupDetails"],
  data: () => ({
    adminUsers: EventBus.adminUsers,
    paramsToBeSent: null,
    currentAdminUser: EventBus.currentAdminUser,
    groups: null,
    emailAlreadySent: false,
    notes: null,
    itemHasChangedClasses: "red white--text font-weight-bold"
  }),
  computed: {
    ...mapState(["currentStudy", "adminUser", "userRole"]),
    isAM: function() {
      return this.currentStudy.params.isAM;
    },
    doseUnit: function() {
      return this.isAM ? "g/U" : "U";
    },
    params: function() {
      return this.currentStudy.params;
    },
    algoPrefix: function() {
      return "doseBreakfast" in this.algoParams ? "dose" : "cr";
    },
    dosePrefix: function() {
      return this.isAM ? "insuCarbRatio" : "insuDose";
    }
  },
  mounted() {
    this.paramsToBeSent = {
      basaDose: this.algoParams[`basalDose`],
      breakfast: this.algoParams[`${this.algoPrefix}Breakfast`],
      lunch: this.algoParams[`${this.algoPrefix}Lunch`],
      supper: this.algoParams[`${this.algoPrefix}Dinner`],
      bedtime: this.algoParams[`${this.algoPrefix}Bedtime`]
    };

    this.emailAlreadySent = this.algoParams.flagged ? true : false;

    firebase
      .database()
      .ref("/Groups/")
      .on("value", snapshot => (this.groups = snapshot.val()));
  },
  methods: {
    fromNow(timestamp) {
      return moment(timestamp).fromNow();
    },
    getChipClass(prop, secProp) {
      if (this.algoParams[`flagged_${prop}`]) {
        return "red white--text font-weight-bold";
      } else if (prop === "basaDose") {
        if (this.algoParams[`basalDose`] != this.params.basaDose) {
          return "primary white--text font-weight-bold";
        }
      } else {
        if (
          this.algoParams[`${this.algoPrefix}${secProp}`] !=
          this.params[this.dosePrefix][prop]
        ) {
          return "primary white--text font-weight-bold";
        }
      }
    },
    flagReviewParams() {
      firebase
        .database()
        .ref("/AdminParamsToReview/")
        .child(this.currentStudy.id)
        .update(
          {
            flagged: true
          },
          error => {
            if (error) {
              console.log(error);
            } else {
              this.emailAlreadySent = true;
            }
          }
        );

      if (this.notes) {
        let obj = {
          note: this.notes,
          study: this.currentStudy.id,
          flagged: true
        };
        this.$store.dispatch("pushNote", obj);

        this.notes = null;
      }
    },
    approveReviewParams() {
      let currentParams = this.params;

      let newParams = {
        acknowledged: false,
        activeInsulinTime: currentParams.activeInsulinTime,
        incrBasal: currentParams.incrBasal,
        incrBolus: currentParams.incrBolus,
        insuSensFactor: currentParams.insuSensFactor,
        isAM: currentParams.isAM,
        targGlucose: currentParams.targGlucose,
        adminUserId: this.adminUser.id,
        timestamp: moment().toISOString()
      };

      let doseKey = currentParams.isAM ? "insuCarbRatio" : "insuDose";
      newParams["basaDose"] = this.paramsToBeSent.basaDose.toString();
      newParams[doseKey] = {
        breakfast: this.paramsToBeSent.breakfast.toString(),
        lunch: this.paramsToBeSent.lunch.toString(),
        supper: this.paramsToBeSent.supper.toString(),
        bedtime: this.paramsToBeSent.bedtime.toString()
      };

      if (this.notes) {
        newParams["privateNotes"] = this.notes;
      }

      // Check if it's an approval or an override
      if (
        this.algoParams[`basalDose`] !== this.paramsToBeSent.basaDose ||
        this.algoParams[`${this.algoPrefix}Breakfast`] !==
          this.paramsToBeSent.breakfast ||
        this.algoParams[`${this.algoPrefix}Lunch`] !==
          this.paramsToBeSent.lunch ||
        this.algoParams[`${this.algoPrefix}Dinner`] !==
          this.paramsToBeSent.supper ||
        this.algoParams[`${this.algoPrefix}Bedtime`] !==
          this.paramsToBeSent.bedtime
      ) {
        newParams["updatedBy"] = "algo-admin-override";
      } else {
        newParams["updatedBy"] = "algo-admin-approved";
      }

      console.log(newParams);

      this.$store.dispatch("approveAlgoParams", newParams);
    }
  }
};
</script>
